// src/pages/printA4InvoicePage.js
// A4-format invoice print page — requires a4InvoiceEnabled feature flag

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NODE_BACKEND_API_URL } from '../config';
import {
    getClientConditionLabel,
    getDisplayUnitPrice,
    getUnitPriceLabel,
    calcIvaIncluido,
    getDiscountConIva,
} from '../utils/invoiceDisplay';
import './printA4InvoicePage.css';

// ── Formatting helpers ─────────────────────────────────────────────────────

/** Format a number as Argentine currency string (no $ prefix) */
const fmt = (n) => {
    const num = parseFloat(n);
    if (isNaN(num)) return '0,00';
    return num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Parse ISO date string → DD/MM/AAAA */
const parseDate = (dateStr) => {
    if (!dateStr) return '';
    const d = dateStr.split('T')[0];
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
};

/** Extract the type letter (A, B, C, X) from tipo_comprobante */
const getDocLetter = (tipoComprobante) => {
    if (!tipoComprobante) return 'X';
    const upper = tipoComprobante.trim().toUpperCase();
    if (upper.endsWith(' A')) return 'A';
    if (upper.endsWith(' B')) return 'B';
    if (upper.endsWith(' C')) return 'C';
    if (upper.endsWith(' X')) return 'X';
    return 'X';
};

/** Map payment_method code → human label */
const paymentLabel = (code) => {
    const map = {
        '201': 'Efectivo',
        '202': 'Cheque',
        '203': 'Transferencia',
        '204': 'Cuenta Corriente',
        '205': 'Tarjeta Débito',
        '206': 'Tarjeta Crédito',
        '207': 'MercadoPago',
        '208': 'Otra',
    };
    return map[String(code)] || 'Efectivo';
};

// ── Component ──────────────────────────────────────────────────────────────

function PrintA4InvoicePage() {
    const { user, isLoadingAuth, interceptorsReady } = useAuth();
    const navigate = useNavigate();

    const [invoice, setInvoice] = useState(null);
    const [org, setOrg] = useState(null);
    const [qrUrl, setQrUrl] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Generate QR data URL once the AFIP QR string is available
    useEffect(() => {
        if (!invoice?.afip_qr) return;
        QRCode.toDataURL(invoice.afip_qr, { width: 140, margin: 1 })
            .then(setQrUrl)
            .catch((e) => console.warn('[PrintA4] QR generation failed:', e));
    }, [invoice]);

    // Fetch invoice + org data
    useEffect(() => {
        if (isLoadingAuth || !interceptorsReady) return;

        // Gate: only users with a4InvoiceEnabled
        if (user && !user.a4InvoiceEnabled) {
            console.warn('[PrintA4] a4InvoiceEnabled is false — redirecting');
            navigate('/Buscador', { replace: true });
            return;
        }

        // Parse invoiceId from hash-router URL: /#/print-a4?invoiceId=42
        const hashParts = window.location.hash.split('?');
        const qs = hashParts.length > 1 ? hashParts[1] : window.location.search.substring(1);
        const params = new URLSearchParams(qs);
        const invoiceId = params.get('invoiceId');

        if (!invoiceId) {
            setError('No se especificó un ID de factura.');
            setLoading(false);
            return;
        }

        const fetchAll = async () => {
            try {
                const [invoiceRes, orgRes] = await Promise.all([
                    axios.get(`${NODE_BACKEND_API_URL}/invoices/${invoiceId}`),
                    axios.get(`${NODE_BACKEND_API_URL}/getclientquotedata`),
                ]);

                if (!invoiceRes.data?.success) {
                    throw new Error('El servidor no devolvió datos de factura.');
                }

                const invData = invoiceRes.data.data;
                setInvoice(invData);

                const orgRaw = Array.isArray(orgRes.data) ? orgRes.data[0] : orgRes.data;
                if (orgRaw) {
                    setOrg({
                        name:    (orgRaw.Name    || orgRaw.name    || '').replace(/[\r\n]/g, '').trim(),
                        address: (orgRaw.Address || orgRaw.address || '').replace(/[\r\n]/g, '').trim(),
                        cuit:    (orgRaw.CUIT    || orgRaw.cuit    || '').replace(/[\r\n]/g, '').trim(),
                        phone:   (orgRaw.Phone   || orgRaw.phone   || '').replace(/[\r\n]/g, '').trim(),
                    });
                }

                // Set document title
                const docNum = invData.numero_comprobante;
                document.title = docNum
                    ? `Factura_${docNum.replace(/\//g, '-')}`
                    : `Factura_${invoiceId}`;

                setLoading(false);
            } catch (err) {
                console.error('[PrintA4] fetch error:', err);
                if (err.response?.status === 404) {
                    setError('Factura no encontrada.');
                } else if (err.response?.status === 401) {
                    setError('Sesión expirada. Por favor, inicie sesión nuevamente.');
                } else {
                    setError('Error al cargar la factura.');
                }
                setLoading(false);
            }
        };

        fetchAll();
    }, [isLoadingAuth, interceptorsReady, user, navigate]);

    // ── Loading / Error ──────────────────────────────────────────────────────

    if (isLoadingAuth || (!invoice && loading)) {
        return (
            <div className="a4-loading">
                <p>Cargando factura...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="a4-error">
                <h2>Error</h2>
                <p>{error}</p>
            </div>
        );
    }

    if (!invoice) return null;

    // ── Derived values ───────────────────────────────────────────────────────

    const tipoDoc = invoice.tipo_comprobante || 'FACTURA';
    const letra   = getDocLetter(tipoDoc);
    const isFacturaA = letra === 'A';

    // Punto de venta + número de comprobante
    const puntoVenta = String(invoice.punto_venta || '0001').padStart(4, '0');
    let compNum = '';
    if (invoice.numero_comprobante) {
        const parts = String(invoice.numero_comprobante).split('-');
        compNum = (parts.length === 2 ? parts[1] : parts[0]).padStart(8, '0');
    }

    // Financial breakdown
    // Backend stores:
    //   subtotal: WITHOUT IVA, BEFORE discount
    //   descuento_importe: discount amount
    //   total: WITH IVA, AFTER discount
    const subtotalBruto    = parseFloat(invoice.subtotal)          || 0;
    const discountAmount   = parseFloat(invoice.descuento_importe) || 0;
    const discountPct      = parseFloat(invoice.descuento_porcentaje) || 0;
    const netBase          = subtotalBruto - discountAmount;   // neto gravado after discount
    const total            = parseFloat(invoice.total)         || 0;
    const ivaAmount        = total - netBase;                  // IVA 21% (for Factura A)

    // Parse items
    // invoice_items stores precio_unitario as SIN IVA (AFIP standard).
    // Per ARCA Ley de Transparencia Fiscal:
    //   - A / NC A → display NETO (sin IVA)
    //   - B / C / NC B / NC X → display FINAL (IVA incluido)
    const items = (invoice.items || []).map((item) => {
        const rawPrice  = parseFloat(item.precio_unitario) || 0;
        const qty       = parseFloat(item.cantidad)        || 1;
        const alicuota  = parseFloat(item.alicuota_iva)    || 21;
        const unitPrice = getDisplayUnitPrice(rawPrice, alicuota, tipoDoc);
        const lineTotal = unitPrice * qty;
        const desc = (item.descripcion || '').replace(/-test_de_servicio$/i, '').trim();

        return {
            code:       item.product_code  || '',
            description: desc,
            brand:      item.brand_name    || '',
            qty,
            unitPrice,
            lineTotal,
        };
    });

    // Org IVA condition comes from the authenticated user's JWT
    const orgCondicion = user?.condicionImpositiva
        ? user.condicionImpositiva.charAt(0).toUpperCase() + user.condicionImpositiva.slice(1).toLowerCase()
        : 'Responsable Inscripto';

    const clientCondicion = getClientConditionLabel(invoice.cliente_tipo);
    const condVenta       = paymentLabel(invoice.payment_method);
    const unitPriceLabel  = getUnitPriceLabel(tipoDoc);

    return (
        <div className="a4-page-wrapper">

            {/* ── Toolbar (hidden on print) ─────────────────────────── */}
            <div className="a4-toolbar no-print">
                <button className="a4-btn-print" onClick={() => window.print()}>
                    🖨️&nbsp;Imprimir
                </button>
            </div>

            {/* ── A4 document ───────────────────────────────────────── */}
            <div className="a4-document" id="a4-print-content">

                {/* ── Header ────────────────────────────────────────── */}
                <div className="a4-header">

                    {/* Left: issuer */}
                    <div className="a4-header-left">
                        <div className="a4-org-name">{org?.name || ''}</div>
                        {org?.address && (
                            <div className="a4-org-detail">Domicilio Comercial: {org.address}</div>
                        )}
                        <div className="a4-org-detail">
                            Condición frente al IVA: {orgCondicion}
                        </div>
                        {org?.cuit && (
                            <div className="a4-org-detail">CUIT: {org.cuit}</div>
                        )}
                        {org?.phone && (
                            <div className="a4-org-detail">Tel: {org.phone}</div>
                        )}
                    </div>

                    {/* Center: letter box */}
                    <div className="a4-header-center">
                        <div className="a4-letter-box">
                            <span className="a4-copy-label">ORIGINAL</span>
                            <span className="a4-letter">{letra}</span>
                            <span className="a4-doc-type-label">{tipoDoc}</span>
                        </div>
                        <div className="a4-pto-venta">Pto. de Venta: {puntoVenta}</div>
                    </div>

                    {/* Right: invoice number + dates */}
                    <div className="a4-header-right">
                        <div className="a4-invoice-number">
                            N° {puntoVenta}-{compNum}
                        </div>
                        <div className="a4-header-field">
                            <span className="a4-field-label">Fecha de Emisión:</span>
                            <span className="a4-field-value">{parseDate(invoice.fecha_emision)}</span>
                        </div>
                        {org?.cuit && (
                            <div className="a4-header-field">
                                <span className="a4-field-label">CUIT Emisor:</span>
                                <span className="a4-field-value">{org.cuit}</span>
                            </div>
                        )}
                        {invoice.fecha_vencimiento && (
                            <div className="a4-header-field">
                                <span className="a4-field-label">Vto. Pago:</span>
                                <span className="a4-field-value">{parseDate(invoice.fecha_vencimiento)}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Client section ────────────────────────────────── */}
                <div className="a4-client-section">
                    <div className="a4-client-row">
                        <div className="a4-client-field a4-client-field-normal">
                            <span className="a4-field-label">CUIT / DNI:</span>
                            <span className="a4-field-value">{invoice.cliente_documento || '-'}</span>
                        </div>
                        <div className="a4-client-field a4-client-field-wide">
                            <span className="a4-field-label">Apellido y Nombre / Razón Social:</span>
                            <span className="a4-field-value">
                                {invoice.cliente_razon_social || 'Consumidor Final'}
                            </span>
                        </div>
                        <div className="a4-client-field a4-client-field-normal">
                            <span className="a4-field-label">Condición IVA:</span>
                            <span className="a4-field-value">{clientCondicion}</span>
                        </div>
                    </div>
                    <div className="a4-client-row">
                        <div className="a4-client-field a4-client-field-normal">
                            <span className="a4-field-label">Condición de Venta:</span>
                            <span className="a4-field-value">{condVenta}</span>
                        </div>
                        {invoice.external_reference && (
                            <div className="a4-client-field a4-client-field-normal">
                                <span className="a4-field-label">Referencia:</span>
                                <span className="a4-field-value">{invoice.external_reference}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Items table ───────────────────────────────────── */}
                <table className="a4-items-table">
                    <thead>
                        <tr>
                            <th className="a4-col-code">Código</th>
                            <th className="a4-col-desc">Descripción</th>
                            <th className="a4-col-qty">Cant.</th>
                            <th className="a4-col-umed">U.Med.</th>
                            <th className="a4-col-price">
                                {unitPriceLabel}
                            </th>
                            {isFacturaA && <th className="a4-col-iva">Alíc. IVA</th>}
                            <th className="a4-col-subtotal">Subtotal</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, i) => (
                            <tr key={i} className={i % 2 !== 0 ? 'a4-row-alt' : ''}>
                                <td className="a4-col-code">{item.code}</td>
                                <td className="a4-col-desc">
                                    {item.description}
                                    {item.brand && (
                                        <span className="a4-brand">{item.brand}</span>
                                    )}
                                </td>
                                <td className="a4-col-qty">{item.qty}</td>
                                <td className="a4-col-umed">UN</td>
                                <td className="a4-col-price">{fmt(item.unitPrice)}</td>
                                {isFacturaA && (
                                    <td className="a4-col-iva">21%</td>
                                )}
                                <td className="a4-col-subtotal">{fmt(item.lineTotal)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* ── Totals ────────────────────────────────────────── */}
                <div className="a4-totals-wrapper">
                    <div className="a4-totals">
                        {isFacturaA ? (
                            <>
                                {discountPct > 0 && (
                                    <>
                                        <div className="a4-total-row">
                                            <span className="a4-total-label">Subtotal Bruto Neto:</span>
                                            <span className="a4-total-value">{fmt(subtotalBruto)}</span>
                                        </div>
                                        <div className="a4-total-row">
                                            <span className="a4-total-label">
                                                Descuento ({discountPct}%):
                                            </span>
                                            <span className="a4-total-value">
                                                − {fmt(discountAmount)}
                                            </span>
                                        </div>
                                    </>
                                )}
                                <div className="a4-total-row">
                                    <span className="a4-total-label">Importe Neto Gravado:</span>
                                    <span className="a4-total-value">{fmt(netBase)}</span>
                                </div>
                                <div className="a4-total-row">
                                    <span className="a4-total-label">IVA 21%:</span>
                                    <span className="a4-total-value">{fmt(ivaAmount)}</span>
                                </div>
                                <div className="a4-total-row a4-total-final">
                                    <span className="a4-total-label">Importe Total:</span>
                                    <span className="a4-total-value">$ {fmt(total)}</span>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Ley de Transparencia Fiscal (RG 1415 / RG 4540):
                                    B / C / NC B / NC X show ONE final price.
                                    Subtotal is computed as Total + Discount (rounding-safe). */}
                                {discountPct > 0 && (() => {
                                    const discountConIva = getDiscountConIva(discountAmount);
                                    const subtotalConIva = total + discountConIva;
                                    return (
                                        <>
                                            <div className="a4-total-row">
                                                <span className="a4-total-label">Subtotal (IVA incluido):</span>
                                                <span className="a4-total-value">{fmt(subtotalConIva)}</span>
                                            </div>
                                            <div className="a4-total-row">
                                                <span className="a4-total-label">
                                                    Descuento ({discountPct}%):
                                                </span>
                                                <span className="a4-total-value">
                                                    − {fmt(discountConIva)}
                                                </span>
                                            </div>
                                        </>
                                    );
                                })()}
                                {discountPct === 0 && (
                                    <div className="a4-total-row">
                                        <span className="a4-total-label">Subtotal (IVA incluido):</span>
                                        <span className="a4-total-value">{fmt(total)}</span>
                                    </div>
                                )}
                                <div className="a4-total-row a4-total-final">
                                    <span className="a4-total-label">Importe Total:</span>
                                    <span className="a4-total-value">$ {fmt(total)}</span>
                                </div>
                                <div
                                    className="a4-total-row a4-total-iva-leyenda"
                                    style={{ fontStyle: 'italic', fontSize: '0.9em', color: '#555' }}
                                >
                                    <span className="a4-total-label">El precio incluye IVA 21%:</span>
                                    <span className="a4-total-value">{fmt(calcIvaIncluido(total))}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* ── Footer spacer (pushes CAE to bottom) ─────────── */}
                <div className="a4-footer-spacer" />

                {/* ── CAE / ARCA section ────────────────────────────── */}
                {invoice.cae && (
                    <div className="a4-cae-section">
                        <div className="a4-cae-info">
                            <p><strong>CAE N°:</strong> {invoice.cae}</p>
                            <p>
                                <strong>Fecha de Vto. del CAE:</strong>{' '}
                                {parseDate(invoice.cae_vencimiento)}
                            </p>
                        </div>
                        {qrUrl ? (
                            <div className="a4-qr-container">
                                <img src={qrUrl} alt="ARCA QR" className="a4-qr-img" />
                                <div className="a4-qr-label">Verificar en ARCA</div>
                            </div>
                        ) : (
                            invoice.afip_qr && (
                                <div className="a4-qr-container">
                                    <div className="a4-qr-label">Generando QR...</div>
                                </div>
                            )
                        )}
                    </div>
                )}

            </div>
        </div>
    );
}

export default PrintA4InvoicePage;
